import { FC, memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
// Import UI Components
import { Col, Icon, Popover, Row, Tag, Tooltip } from '@geneui/components';
import SectionWithTitle from 'components/common/SectionWithTitle';
// Import Constants
import { l } from 'constants/common';
import { ABTestStatusLabels } from 'constants/abTest';
// Import Enums
import { ABTestFailReasonEnum, ABTestStatusEnum } from 'enums';
// Import Styles
import styles from './campaignReportABTest.module.scss';

interface Props {
    generalDetails: {
        goal: string;
        segmentSplitEndDate: string;
        kpiNames: string[];
        endDate: string;
        controlGroup: number;
        startDate: string;
        pilotGroup: number;
        targetCount: number;
        controlCount: number;
        isExclusive: boolean;
        exclusionPeriod: Nullable<number>;
        status: ABTestStatusEnum;
        reason: ABTestFailReasonEnum;
    };
}

const ABTestGeneralDetails: FC<Props> = ({ generalDetails }) => {
    const {
        goal,
        segmentSplitEndDate,
        kpiNames,
        endDate,
        controlGroup,
        startDate,
        pilotGroup,
        targetCount,
        controlCount,
        isExclusive,
        status,
        reason,
    } = generalDetails;
    const { t } = useTranslation();

    const [isOpenKPIs, setIsOpenKPIs] = useState<boolean>(false);

    const kPIsClickHandler = () => setIsOpenKPIs((prev) => !prev);

    const getPopoverContent = () => (
        <div className="used-in-content">
            <div className="tagList-bc c-tagList-bc">
                {kpiNames.map((title, index) => {
                    return (
                        <Tag
                            key={index}
                            name={t(l[`TestMetric${title}`])}
                            cornerRadius="smooth-radius"
                            appearance="minimal"
                            flexibility="compact"
                        />
                    );
                })}
            </div>
        </div>
    );

    const statusLabel = useMemo(() => {
        return (
            <div className={classNames(styles.statusLabel, styles[ABTestStatusEnum[status]])}>
                {t(l[ABTestStatusLabels[status]])}
                {reason !== ABTestFailReasonEnum.None && (
                    <Tooltip text={t(l[`ABTestFailReason${ABTestFailReasonEnum[reason]}`])}>
                        <Icon className="bc-icon-info-48" />
                    </Tooltip>
                )}
            </div>
        );
    }, [status, reason, t]);

    return (
        <SectionWithTitle title={t(l.GeneralDetails)} titleContent={statusLabel}>
            <Row>
                <Col size={3} className="ab-test-general-details-column-info-item">
                    <p className="g-details-title">{t(l.SplitSegmentBy)}</p>
                    <p className="g-details-subtitle">{t(l[goal])}</p>
                </Col>
                <Col size={3} className="ab-test-general-details-column-info-item">
                    <p className="g-details-title">{t(l.MetricsForReport)}</p>
                    <p className="g-details-subtitle">
                        <Popover
                            className="used-in-popover"
                            isOpen={isOpenKPIs}
                            extendTargetWidth={false}
                            Content={getPopoverContent()}
                        >
                            <button onClick={kPIsClickHandler} className="used-in-button">
                                {kpiNames.length}
                            </button>
                        </Popover>
                    </p>
                </Col>
                <Col size={3} className="ab-test-general-details-column-info-item">
                    <p className="g-details-title">
                        {t(l.ControlGroup)}
                        <Tooltip text={t(l.ABTestControlGroupPercentageTooltip)}>
                            <Icon className="bc-icon-info-48" />
                        </Tooltip>
                    </p>
                    <p className="g-details-subtitle">
                        {controlGroup}% / {controlCount}
                    </p>
                </Col>
                <Col size={3} className="ab-test-general-details-column-info-item">
                    <p className="g-details-title">
                        {t(l.PilotGroup)}
                        <Tooltip text={t(l.ABTestPilotGroupPercentageTooltip)}>
                            <Icon className="bc-icon-info-48" />
                        </Tooltip>
                    </p>
                    <p className="g-details-subtitle">
                        {pilotGroup}% / {targetCount}
                    </p>
                </Col>
                <Col size={3} className="ab-test-general-details-column-info-item">
                    <p className="g-details-title">{t(l.ClientsCount)}</p>
                    <p className="g-details-subtitle">{targetCount + controlCount}</p>
                </Col>
                <Col size={3} className="ab-test-general-details-column-info-item">
                    <p className="g-details-title">{t(l.ExclusiveMode)}</p>
                    <p className="g-details-subtitle">{isExclusive ? t(l.Enabled) : t(l.Disabled)}</p>
                </Col>
                <Col size={3} className="ab-test-general-details-column-info-item">
                    <p className="g-details-title">{t(l.StartDate)}</p>
                    <p className="g-details-subtitle">{startDate}</p>
                </Col>
                <Col size={3} className="ab-test-general-details-column-info-item">
                    <p className="g-details-title">{t(l.SegmentSplitEndDate)}</p>
                    <p className="g-details-subtitle">{segmentSplitEndDate}</p>
                </Col>
                <Col size={3} className="ab-test-general-details-column-info-item">
                    <p className="g-details-title">{t(l.EndDate)}</p>
                    <p className="g-details-subtitle">{endDate}</p>
                </Col>
            </Row>
        </SectionWithTitle>
    );
};

export default memo(ABTestGeneralDetails);
